import addIcon from '@/assets/image/icons/plus.svg';
import closeIcon from '@/assets/image/icons/close.svg';
import {
  CustomerFinder,
  CustomerItemType,
} from '@/contact/person/customer-finder/customer-finder.component';
import { useErrorLog } from '@/error/error.provider';
import { CheckoutItemType, Gender } from '@/graphql/generated/types';
import {
  ProductFinder,
  ProductItemType,
} from '@/product/product-finder/product-finder.component';
import { Profile } from '@/profile/profile';
import { Box } from '@/ui/box/box.component';
import { Button } from '@/ui/button/button.component';
import { Checkbox } from '@/ui/checkbox/checkbox.component';
import { Formatted } from '@/ui/formatted/formatted.component';
import { GenderInputField } from '@/ui/gender-input-field/gender-input-field.component';
import { Grid } from '@/ui/grid/grid.component';
import { CurrencyInputField } from '@/ui/currency-input-field/currency-input-field.component';
import { DatetimeField } from '@/ui/datetime-field/datetime-field.component';
import { InputField } from '@/ui/input-field/input-field.component';
import { LabeledItem } from '@/ui/labeled-item/labeled-item.component';
import { Loader } from '@/ui/loader/loader.component';
import { useConfirm } from '@/ui/modal/modal.hooks';
import { Row } from '@/ui/table/row.component';
import { Table } from '@/ui/table/table.component';
import { Textarea } from '@/ui/textarea/textarea.component';
import { error as showError, success } from '@/ui/toaster/toast';
import { yupResolver } from '@hookform/resolvers/yup';
import { navigate, RouteComponentProps } from '@reach/router';
import { isBefore, parseISO, startOfDay } from 'date-fns';
import { uniqueId } from 'lodash-es';
import React, { ComponentType, useCallback, useEffect, useState } from 'react';
import {
  Controller,
  UseFormReturn,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import {
  GoogleAddressAutocomplete,
  ParsedAddress,
} from '@/contact/shared/address/google-address-autocomplete.component';
import {
  CourseUnitFinder,
  CourseUnitOrModuleItemType,
} from 'shared/course/course-unit-finder/course-unit-finder.component';
import { array, mixed, object, string, boolean } from 'yup';
import { Frame } from '../../frame';
import { useUpdateCustomerMutation } from '@/contact/person/editor/customer/editor.generated';
import { useCreateOrderMutation } from '../shared/create-order/create-order.generated';
import { OrderItemType } from '../shared/create-order/order-item/order-items.hooks';
import styles from './create-order.module.scss';
import {
  PreviewCheckoutQuery,
  usePreviewCheckoutLazyQuery,
} from './preview-checkout.generated';

type CustomerMode = 'existing' | 'new';

type AddressFields = {
  streetAndNumber: string;
  postalCode: string;
  city: string;
};

type InvoiceAddressFields = {
  company: string;
  streetAndNumber: string;
  postalCode: string;
  city: string;
  invoiceEmail: string;
};

type Values = {
  customerMode: CustomerMode;
  customer: CustomerItemType | null;
  updateCustomerNumber: string;
  newCustomer: {
    gender?: Gender | null;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    customerNumber: string;
  };
  updateExistingCustomer: {
    gender?: Gender | null;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  address: AddressFields;
  useInvoiceAddress: boolean;
  invoiceAddress: InvoiceAddressFields;
  items: OrderItemType[];
  sendNotificationMails: boolean;
  consentAds: boolean;
  comment?: string;
  voucherCode: string;
  discountCheckboxChecked: boolean;
};

type Step = 'form' | 'summary';

const schema = object({
  customerMode: string().oneOf(['existing', 'new']).required(),
  customer: object()
    .nullable()
    .when('customerMode', {
      is: 'existing',
      then: (s) =>
        s
          .required('Bitte wähle eine:n Kunde:in aus.')
          .test(
            'has-value',
            'Bitte wähle eine:n Kunde:in aus.',
            (v: any) => v && v.value,
          ),
    }),
  newCustomer: object().when('customerMode', {
    is: 'new',
    then: () =>
      object({
        gender: mixed<Gender>().nullable(),
        firstName: string().required('Vorname ist ein Pflichtfeld.'),
        lastName: string().required('Nachname ist ein Pflichtfeld.'),
        email: string()
          .email('Bitte gib eine gültige E-Mail Adresse ein.')
          .required('E-Mail ist ein Pflichtfeld.'),
        phone: string().nullable(),
      }),
  }),
  address: object().when('customerMode', {
    is: 'new',
    then: () =>
      object({
        streetAndNumber: string().required('Straße ist ein Pflichtfeld.'),
        postalCode: string().required('PLZ ist ein Pflichtfeld.'),
        city: string().required('Ort ist ein Pflichtfeld.'),
      }),
  }),
  invoiceAddress: object().when('useInvoiceAddress', {
    is: true,
    then: () =>
      object({
        company: string().required('Arbeitgebername ist ein Pflichtfeld.'),
        streetAndNumber: string().required('Straße ist ein Pflichtfeld.'),
        postalCode: string().required('PLZ ist ein Pflichtfeld.'),
        city: string().required('Ort ist ein Pflichtfeld.'),
      }),
  }),
  items: array()
    .of(
      object({
        startDate: string().nullable(),
        endDate: string()
          .nullable()
          .when('startDate', {
            is: (startDate: string | null) => !!startDate,
            then: (s) =>
              s.required('Bei einem Startdatum ist ein Enddatum notwendig.'),
          })
          .test(
            'not-before-start',
            'Das Enddatum darf nicht vor dem Startdatum (bzw. dem Buchungsdatum) liegen.',
            function (endDate) {
              if (!endDate) return true;
              const startDate = this.parent.startDate
                ? parseISO(this.parent.startDate)
                : new Date();
              return !isBefore(
                startOfDay(parseISO(endDate)),
                startOfDay(startDate),
              );
            },
          ),
      }),
    )
    .min(1, 'Mindestens eine Position ist notwendig.')
    .test(
      'has-selected-items',
      'Mindestens eine Position muss ausgewählt sein.',
      (items: any) => items?.some((i: any) => i.item != null),
    ),
  sendNotificationMails: boolean(),
  consentAds: boolean(),
  comment: string().nullable(),
});

function buildCartItems(values: Values) {
  return values.items
    .filter((item) => !!item.item)
    .map((item) => {
      if (item.type === 'Course' && item.item) {
        const courseItem = item.item as CourseUnitOrModuleItemType;
        return {
          type:
            courseItem.type === 'CourseModule'
              ? CheckoutItemType.CourseModule
              : CheckoutItemType.CourseUnit,
          id: courseItem.value,
          quantity: 1,
          amount: parseFloat(item.amount as any),
        };
      }
      return {
        type: CheckoutItemType.Product,
        id: item.item!.value,
        quantity: 1,
        amount: parseFloat(item.amount as any),
        // Always sent explicitly, so clearing the dates overrides the product.
        startDate: item.startDate || null,
        endDate: item.endDate || null,
      };
    });
}

function buildInvoiceAddress(values: Values) {
  const hasInvoiceAddress =
    values.useInvoiceAddress &&
    (values.invoiceAddress.company ||
      values.invoiceAddress.streetAndNumber ||
      values.invoiceAddress.postalCode ||
      values.invoiceAddress.city);
  if (!hasInvoiceAddress) return undefined;
  return {
    company: values.invoiceAddress.company || undefined,
    streetAndNumber: values.invoiceAddress.streetAndNumber || undefined,
    postalCode: values.invoiceAddress.postalCode || undefined,
    city: values.invoiceAddress.city || undefined,
  };
}

function buildCustomerVars(values: Values) {
  if (values.customerMode === 'existing') {
    return {
      customerId: values.customer?.customerId,
      customerNumber: values.updateCustomerNumber || undefined,
      contact: undefined,
      address: {},
      invoiceAddress: buildInvoiceAddress(values),
    };
  }
  return {
    customerId: undefined,
    contact: {
      gender: values.newCustomer.gender || undefined,
      firstName: values.newCustomer.firstName,
      lastName: values.newCustomer.lastName,
      email: values.newCustomer.email,
      phone: values.newCustomer.phone || undefined,
      invoiceEmail: values.invoiceAddress.invoiceEmail || undefined,
    },
    customerNumber: values.newCustomer.customerNumber || undefined,
    address: values.address,
    invoiceAddress: buildInvoiceAddress(values),
  };
}

export const CreateOrder: ComponentType<RouteComponentProps> = () => {
  const profile = Profile.use();
  const { log } = useErrorLog();
  const [step, setStep] = useState<Step>('form');

  const form = useForm<Values>({
    resolver: yupResolver(schema as any),
    mode: 'onChange',
    defaultValues: {
      customerMode: 'existing',
      customer: null,
      updateCustomerNumber: '',
      newCustomer: {
        gender: null,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        customerNumber: '',
      },
      updateExistingCustomer: {
        gender: null,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
      address: {
        streetAndNumber: '',
        postalCode: '',
        city: '',
      },
      useInvoiceAddress: false,
      invoiceAddress: {
        company: '',
        streetAndNumber: '',
        postalCode: '',
        city: '',
        invoiceEmail: '',
      },
      items: [],
      sendNotificationMails: false,
      consentAds: false,
      comment: '',
      voucherCode: '',
      discountCheckboxChecked: false,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'items',
  });

  const customerMode = form.watch('customerMode');
  const selectedCustomer = form.watch('customer');
  const [createOrder, { loading: submitting }] = useCreateOrderMutation();
  const [updateCustomer] = useUpdateCustomerMutation();
  const [fetchPreview, { data: previewData, loading: previewLoading }] =
    usePreviewCheckoutLazyQuery();

  const goToSummary = form.handleSubmit(
    async (values) => {
      if (!profile.selectedEnterprise) {
        showError('Kein Unternehmen ausgewählt.');
        return;
      }

      const items = buildCartItems(values);
      if (items.length === 0) {
        showError('Bitte füge mindestens eine Position hinzu.');
        return;
      }

      const customerVars = buildCustomerVars(values);
      try {
        await fetchPreview({
          variables: {
            enterpriseId: profile.selectedEnterprise.id,
            ...customerVars,
            items,
            comment: values.comment || undefined,
            discountParams: {
              voucherCode: values.voucherCode || undefined,
              discountCheckboxChecked:
                values.discountCheckboxChecked || undefined,
            },
          } as any,
        });
        setStep('summary');
      } catch (e) {
        showError('Vorschau konnte nicht geladen werden.');
        log(JSON.stringify(e));
      }
    },
    () => {
      showError('Bitte überprüfe deine Angaben.');
    },
  );

  const submitOrder = useCallback(async () => {
    const values = form.getValues();
    if (!profile.selectedEnterprise) return;

    if (values.customerMode === 'existing' && values.customer) {
      const ec = values.updateExistingCustomer;
      const invoiceAddr = buildInvoiceAddress(values);
      try {
        await updateCustomer({
          variables: {
            id: values.customer.value,
            personInput: {
              firstName: ec.firstName || undefined,
              lastName: ec.lastName || undefined,
              gender: ec.gender || undefined,
              address: {
                streetAndNumber: values.address.streetAndNumber || undefined,
                postalCode: values.address.postalCode || undefined,
                city: values.address.city || undefined,
              },
              invoiceAddress: invoiceAddr
                ? {
                    company: invoiceAddr.company,
                    streetAndNumber: invoiceAddr.streetAndNumber,
                    postalCode: invoiceAddr.postalCode,
                    city: invoiceAddr.city,
                  }
                : undefined,
            },
            userInput: {
              username: values.customer.username ?? undefined,
              status: values.customer.status ?? undefined,
              customerNumber: values.updateCustomerNumber || undefined,
              invoiceEmail: values.invoiceAddress.invoiceEmail || undefined,
            },
          },
        });
      } catch (e) {
        showError('Kundendaten konnten nicht gespeichert werden.');
        log(JSON.stringify(e));
        return;
      }
    }

    const customerVars = buildCustomerVars(values);
    try {
      const result = await createOrder({
        variables: {
          enterpriseId: profile.selectedEnterprise.id,
          ...customerVars,
          sendNotificationMails: values.sendNotificationMails,
          consentGtc: 'Bestellung wurde durch Administrator erzeugt.',
          consentAds: values.consentAds
            ? 'Bestellung wurde durch Administrator erzeugt, Zustimmung zum Versenden von Info-Material wurde eingeholt.'
            : undefined,
          comment: values.comment || undefined,
          items: buildCartItems(values),
          discountParams: {
            voucherCode: values.voucherCode || undefined,
            discountCheckboxChecked:
              values.discountCheckboxChecked || undefined,
          },
        } as any,
      });

      success('Die Bestellung wurde erfolgreich erstellt.');

      const orderId = result.data?.adminCheckout?.orderId;
      if (orderId) {
        navigate(`/orders/${orderId}/view`);
      } else {
        navigate('/orders/list');
      }
    } catch (e) {
      showError('Bei der Bestellung ist ein Fehler aufgetreten.');
      log(JSON.stringify(e));
    }
  }, [form, profile.selectedEnterprise, createOrder, updateCustomer, log]);

  const [confirmModal, askConfirm] = useConfirm(submitOrder, {
    title: 'Bestellung erstellen',
    meaning: 'success',
  });

  if (step === 'summary') {
    return (
      <SummaryStep
        preview={previewData?.previewCheckout ?? null}
        loading={previewLoading}
        form={form}
        submitting={submitting}
        confirmModal={confirmModal}
        onConfirm={() =>
          askConfirm('Soll die Bestellung jetzt verbindlich erstellt werden?')
        }
        onBack={() => setStep('form')}
      />
    );
  }

  return (
    <>
      <Frame.SubTitle>Neue Bestellung</Frame.SubTitle>
      <Frame.ActionBar>
        <Button label="Zurück" linkTo="/orders/list" />
        <Button
          primary
          label="Weiter zur Zusammenfassung"
          onClick={goToSummary}
        />
      </Frame.ActionBar>
      <Frame.Content>
        <Grid.Row>
          <Grid.Column bigger>
            <Box title="Positionen" withMargin>
              {fields.length === 0 && (
                <div className={styles.emptyItems}>
                  Noch keine Positionen hinzugefügt.
                </div>
              )}
              <div className={styles.itemList}>
                {fields.map((item, index) => (
                  <OrderItemRow
                    key={item.id}
                    index={index}
                    item={item}
                    form={form}
                    onRemove={() => remove(index)}
                  />
                ))}
              </div>
              {form.formState.errors.items?.message && (
                <p style={{ color: '#b3404a', marginTop: '0.5rem' }}>
                  {form.formState.errors.items.message}
                </p>
              )}
              <div className={styles.addButtons}>
                <Button
                  icon={addIcon}
                  small
                  primary
                  label="Kurs-Einheit / -Modul"
                  onClick={() =>
                    append({
                      id: uniqueId(),
                      quantity: 1,
                      item: null,
                      amount: 0,
                      type: 'Course',
                    })
                  }
                />
                <Button
                  icon={addIcon}
                  small
                  primary
                  label="Produkt"
                  onClick={() =>
                    append({
                      id: uniqueId(),
                      quantity: 1,
                      item: null,
                      amount: 0,
                      type: 'Product',
                      startDate: null,
                      endDate: null,
                    })
                  }
                />
              </div>
            </Box>

            <Box title="Optionen" withMargin>
              <Controller
                name="comment"
                control={form.control}
                render={({ field }) => (
                  <Textarea
                    slim
                    label="Kommentar zur Bestellung (einsehbar durch Kund:in)"
                    {...field}
                  />
                )}
              />
              <Controller
                name="voucherCode"
                control={form.control}
                render={({ field }) => (
                  <InputField label="Voucher-Code" {...field} />
                )}
              />
              <Controller
                name="discountCheckboxChecked"
                control={form.control}
                render={({ field }) => (
                  <Checkbox
                    label="Rabatt-Checkbox aktivieren"
                    {...field}
                    checked={field.value}
                  />
                )}
              />
            </Box>
          </Grid.Column>

          <Grid.Column smaller>
            <Box title="Kunde:in" withMargin>
              <div className={styles.customerToggle}>
                <Button
                  small
                  primary={customerMode === 'existing'}
                  label="Bestehende:r Kunde:in"
                  onClick={() => form.setValue('customerMode', 'existing')}
                />
                <Button
                  small
                  primary={customerMode === 'new'}
                  label="Neue:r Kunde:in"
                  onClick={() => form.setValue('customerMode', 'new')}
                />
              </div>

              {customerMode === 'existing' && (
                <>
                  <Controller
                    name="customer"
                    control={form.control}
                    render={({ field }) => (
                      <CustomerFinder
                        value={field.value}
                        onChange={(item) => {
                          field.onChange(item);
                          form.setValue('updateCustomerNumber', item?.customerNumber ?? '');
                          form.setValue('updateExistingCustomer.gender', item?.gender ?? null);
                          form.setValue('updateExistingCustomer.firstName', item?.firstName ?? '');
                          form.setValue('updateExistingCustomer.lastName', item?.lastName ?? '');
                          form.setValue('updateExistingCustomer.email', item?.email ?? '');
                          form.setValue('updateExistingCustomer.phone', '');
                          form.setValue('address.streetAndNumber', item?.address?.streetAndNumber ?? '');
                          form.setValue('address.postalCode', item?.address?.postalCode ?? '');
                          form.setValue('address.city', item?.address?.city ?? '');
                          const hasInvoiceAddress = !!(item?.invoiceAddress?.streetAndNumber || item?.invoiceAddress?.city);
                          form.setValue('useInvoiceAddress', hasInvoiceAddress);
                          form.setValue('invoiceAddress.company', item?.invoiceAddress?.company ?? '');
                          form.setValue('invoiceAddress.streetAndNumber', item?.invoiceAddress?.streetAndNumber ?? '');
                          form.setValue('invoiceAddress.postalCode', item?.invoiceAddress?.postalCode ?? '');
                          form.setValue('invoiceAddress.city', item?.invoiceAddress?.city ?? '');
                          form.setValue('invoiceAddress.invoiceEmail', item?.invoiceEmail ?? '');
                        }}
                      />
                    )}
                  />
                  {form.formState.errors.customer?.message && (
                    <p style={{ color: '#b3404a', marginTop: '0.5rem' }}>
                      {form.formState.errors.customer.message}
                    </p>
                  )}
                  {selectedCustomer && (
                    <ExistingCustomerEditor form={form} />
                  )}
                </>
              )}

              {customerMode === 'new' && (
                <>
                  <div className={styles.customerFields}>
                    <GoogleAddressAutocomplete
                      onSelect={(address: ParsedAddress) => {
                        form.setValue('address.streetAndNumber', address.streetAndNumber ?? '');
                        form.setValue('address.postalCode', address.postalCode ?? '');
                        form.setValue('address.city', address.city ?? '');
                      }}
                    />
                    <Controller
                      name="newCustomer.gender"
                      control={form.control}
                      render={({ field }) => (
                        <GenderInputField
                          label="Anrede"
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    <Controller
                      name="newCustomer.firstName"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <InputField
                          label="Vorname"
                          required
                          error={fieldState.error?.message}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="newCustomer.lastName"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <InputField
                          label="Nachname"
                          required
                          error={fieldState.error?.message}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="address.streetAndNumber"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <InputField
                          label="Straße"
                          required
                          error={fieldState.error?.message}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="address.postalCode"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <InputField
                          label="PLZ"
                          required
                          error={fieldState.error?.message}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="address.city"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <InputField
                          label="Ort"
                          required
                          error={fieldState.error?.message}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="newCustomer.email"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <InputField
                          label="E-Mail"
                          required
                          error={fieldState.error?.message}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="newCustomer.phone"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <InputField
                          label="Telefon"
                          error={fieldState.error?.message}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="newCustomer.customerNumber"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <InputField
                          label="Kundennummer"
                          error={fieldState.error?.message}
                          {...field}
                        />
                      )}
                    />
                  </div>

                  <NewCustomerInvoiceAddress form={form} />
                </>
              )}
            </Box>
          </Grid.Column>
        </Grid.Row>
      </Frame.Content>
    </>
  );
};

// ─── Summary Step ───────────────────────────────────────────────────────

type PreviewItem = PreviewCheckoutQuery['previewCheckout']['items'][number];

function getPreviewItemDisplayNetAmount(item: PreviewItem): boolean {
  const a = item as any;
  return (
    a.product?.displayNetAmount ||
    a.package?.displayNetAmount ||
    a.courseUnit?.course?.displayNetAmount ||
    a.courseModule?.courseUnit?.course?.displayNetAmount ||
    false
  );
}


interface SummaryStepProps {
  preview: PreviewCheckoutQuery['previewCheckout'] | null;
  loading: boolean;
  form: UseFormReturn<Values>;
  submitting: boolean;
  confirmModal: React.ReactElement;
  onConfirm: () => void;
  onBack: () => void;
}

function SummaryStep({
  preview,
  loading,
  form,
  submitting,
  confirmModal,
  onConfirm,
  onBack,
}: SummaryStepProps) {
  const values = form.getValues();

  return (
    <>
      {confirmModal}
      <Frame.SubTitle>Neue Bestellung — Zusammenfassung</Frame.SubTitle>
      <Frame.ActionBar>
        <Button label="Zurück zum Bearbeiten" onClick={onBack} />
        <Button
          primary
          label="Bestellung erstellen"
          onClick={onConfirm}
          disabled={submitting || loading}
        />
      </Frame.ActionBar>
      <Frame.Content>
        {loading && <Loader />}
        {!loading && preview && (
          <Grid.Row>
            <Grid.Column bigger>
              <Box title="Positionen" withMargin>
                <Table<PreviewItem>
                  compact
                  data={preview.items}
                  columns={[
                    'Position',
                    <th key="price" className="right">
                      Preis
                    </th>,
                    <th key="tax" className="right">
                      MwSt.
                    </th>,
                  ]}
                  render={(item, i) => {
                    const displayNet = getPreviewItemDisplayNetAmount(item);
                    const taxRate = item.taxRate ?? 0;
                    const netAmount = item.amount ?? 0;
                    // amount is always net; show gross unless displayNetAmount=true
                    const displayPrice =
                      !displayNet && taxRate > 0
                        ? netAmount * (1 + taxRate)
                        : netAmount;
                    const discounts =
                      'discounts' in item ? item.discounts : null;
                    return (
                      <React.Fragment key={i}>
                        <Row>
                          <td>
                            {'verboseTitle' in item && item.verboseTitle
                              ? item.verboseTitle
                              : item.title}
                            {'description' in item && item.description && (
                              <div style={{ color: '#666', fontSize: '0.9em' }}>
                                {item.description}
                              </div>
                            )}
                            {'description2' in item && item.description2 && (
                              <div
                                style={{ color: '#666', fontSize: '0.9em' }}
                                dangerouslySetInnerHTML={{
                                  __html: item.description2,
                                }}
                              />
                            )}
                          </td>
                          <td className="numeric">
                            <Formatted.Currency value={displayPrice} />
                          </td>
                          <td className="numeric">
                            {taxRate > 0
                              ? displayNet
                                ? `zzgl. ${Math.round(taxRate * 100)} %`
                                : `inkl. ${Math.round(taxRate * 100)} %`
                              : 'MwSt-frei'}
                          </td>
                        </Row>
                        {discounts?.map((discount, di) => {
                          const discountNet =
                            discount.type === 'Relative'
                              ? -(netAmount * (discount.amount ?? 0))
                              : -(discount.amount ?? 0);
                          const displayDiscount =
                            !displayNet && taxRate > 0
                              ? discountNet * (1 + taxRate)
                              : discountNet;
                          return (
                            <Row key={`${i}-d${di}`}>
                              <td style={{ color: '#666', fontSize: '0.9em' }}>
                                {discount.reason || 'Rabatt'}
                              </td>
                              <td
                                className="numeric"
                                style={{ color: '#666', fontSize: '0.9em' }}
                              >
                                {discount.type === 'Relative'
                                  ? `-${(discount.amount ?? 0 )} %`
                                  : <Formatted.Currency value={displayDiscount} />}
                              </td>
                              <td />
                            </Row>
                          );
                        })}
                      </React.Fragment>
                    );
                  }}
                  additionalRows={
                    <>
                    
                      {preview.globalDiscounts.map((d, i) => (
                        <Row key={`discount-${i}`}>
                          <td>{d.reason || d.type}</td>
                          <td className="numeric">
                            {d.type === 'Relative'
                              ? `-${((d.amount ?? 0) )} %`
                              : <Formatted.Currency value={-(d.amount ?? 0)} />}
                          </td>
                          <td />
                        </Row>
                      ))}
                      <Row><td>&nbsp;</td><td /><td /></Row>
                      {preview.netTotal != null && (
                        <Row>
                          <td>Netto</td>
                          <td className="numeric">
                            <Formatted.Currency value={preview.netTotal} />
                          </td>
                          <td />
                        </Row>
                      )}
                      {preview.taxes?.map((tax, i) => (
                        <Row key={`tax-${i}`}>
                          <td>enthaltene MwSt. {Math.round(tax.taxRate * 100)} %</td>
                          <td className="numeric">
                            <Formatted.Currency value={tax.amount} />
                          </td>
                          <td />
                        </Row>
                      ))}
                      <Row>
                        <td className="total">Gesamt</td>
                        <td className="numeric total">
                          <Formatted.Currency value={preview.grossTotal ?? preview.total} />
                        </td>
                        <td />
                      </Row>
                    </>
                  }
                />
              </Box>
            </Grid.Column>

            <Grid.Column smaller>
              <Box title="Kunde:in" withMargin>
                {values.customerMode === 'existing' && values.customer && (
                  <>
                    <LabeledItem label="Kunde:in" value={values.customer.label} />
                    {values.updateCustomerNumber && (
                      <LabeledItem
                        label="Kundennummer"
                        value={values.updateCustomerNumber}
                      />
                    )}
                    {values.updateExistingCustomer.firstName && (
                      <LabeledItem
                        label="Name"
                        value={`${values.updateExistingCustomer.firstName} ${values.updateExistingCustomer.lastName}`}
                      />
                    )}
                    {values.updateExistingCustomer.email && (
                      <LabeledItem label="E-Mail" value={values.updateExistingCustomer.email} />
                    )}
                    {values.updateExistingCustomer.phone && (
                      <LabeledItem label="Telefon" value={values.updateExistingCustomer.phone} />
                    )}
                    {(values.address.streetAndNumber || values.address.city) && (
                      <LabeledItem
                        label="Adresse"
                        value={[
                          values.address.streetAndNumber,
                          [values.address.postalCode, values.address.city]
                            .filter(Boolean)
                            .join(' '),
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      />
                    )}
                    {values.useInvoiceAddress && (
                      <>
                        <LabeledItem
                          label="Rechnungsadresse"
                          value={[
                            values.invoiceAddress.company,
                            values.invoiceAddress.streetAndNumber,
                            [
                              values.invoiceAddress.postalCode,
                              values.invoiceAddress.city,
                            ]
                              .filter(Boolean)
                              .join(' '),
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        />
                        {values.invoiceAddress.invoiceEmail && (
                          <LabeledItem
                            label="RG-E-Mail"
                            value={values.invoiceAddress.invoiceEmail}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
                {values.customerMode === 'new' && (
                  <>
                    <LabeledItem
                      label="Kunde:in"
                      value={`${values.newCustomer.firstName} ${values.newCustomer.lastName} (neu)`}
                    />
                    {values.newCustomer.customerNumber && (
                      <LabeledItem
                        label="Kundennummer"
                        value={values.newCustomer.customerNumber}
                      />
                    )}
                    <LabeledItem
                      label="E-Mail"
                      value={values.newCustomer.email}
                    />
                    {values.newCustomer.phone && (
                      <LabeledItem
                        label="Telefon"
                        value={values.newCustomer.phone}
                      />
                    )}
                    <LabeledItem
                      label="Adresse"
                      value={[
                        values.address.streetAndNumber,
                        [values.address.postalCode, values.address.city]
                          .filter(Boolean)
                          .join(' '),
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    />
                    {values.useInvoiceAddress && (
                      <>
                        <LabeledItem
                          label="Rechnungsadresse"
                          value={[
                            values.invoiceAddress.company,
                            values.invoiceAddress.streetAndNumber,
                            [
                              values.invoiceAddress.postalCode,
                              values.invoiceAddress.city,
                            ]
                              .filter(Boolean)
                              .join(' '),
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        />
                        {values.invoiceAddress.invoiceEmail && (
                          <LabeledItem
                            label="RG-E-Mail"
                            value={values.invoiceAddress.invoiceEmail}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>

              <Box title="Optionen" withMargin>
                <LabeledItem label="Kommentar" value={values.comment || '–'} />
                {values.voucherCode && (
                  <LabeledItem label="Voucher-Code" value={values.voucherCode} />
                )}
                {values.discountCheckboxChecked && (
                  <LabeledItem label="Rabatt-Checkbox" value="Aktiviert" />
                )}
              </Box>

              <Box title="Benachrichtigungen und Einwilligungen" withMargin>
                <Controller
                  name="sendNotificationMails"
                  control={form.control}
                  render={({ field }) => (
                    <Checkbox
                      label="Bestellbestätigung an Kund:in senden"
                      {...field}
                      checked={field.value}
                    />
                  )}
                />
                <Controller
                  name="consentAds"
                  control={form.control}
                  render={({ field }) => (
                    <Checkbox
                      label="Einwilligung für Werbung (Kund:in erhält eine Double-Opt-In Mail)"
                      {...field}
                      checked={field.value}
                    />
                  )}
                />
              </Box>
            </Grid.Column>
          </Grid.Row>
        )}
      </Frame.Content>
    </>
  );
}

// ─── Order Item Row ─────────────────────────────────────────────────────

interface OrderItemRowProps {
  index: number;
  item: OrderItemType;
  form: UseFormReturn<Values>;
  onRemove: () => void;
}

function OrderItemRow({ index, item, form, onRemove }: OrderItemRowProps) {
  const chosenItem = form.watch(`items.${index}.item`);

  useEffect(() => {
    if (chosenItem) {
      form.clearErrors('items');
      form.setValue(`items.${index}.amount`, chosenItem.amount);
    }
  }, [index, chosenItem]);

  return (
    <div className={styles.itemRow}>
      <div className={styles.itemFinder}>
        <Controller
          name={`items.${index}.item`}
          control={form.control}
          render={({ field }) => (
            <>
              {item.type === 'Course' && (
                <CourseUnitFinder
                  small
                  bookableOnly
                  onChange={field.onChange}
                  value={field.value as CourseUnitOrModuleItemType}
                />
              )}
              {item.type === 'Product' && (
                <ProductFinder
                  onChange={(product) => {
                    field.onChange(product);
                    // Prefill with the product's period, may be overridden.
                    form.setValue(
                      `items.${index}.startDate`,
                      product?.startDate ?? null,
                    );
                    form.setValue(
                      `items.${index}.endDate`,
                      product?.endDate ?? null,
                      { shouldValidate: true },
                    );
                  }}
                  value={field.value as ProductItemType}
                />
              )}
            </>
          )}
        />
      </div>
      {item.type === 'Product' && (
        <>
          <div className={styles.itemDate}>
            <Controller
              name={`items.${index}.startDate`}
              control={form.control}
              render={({ field }) => (
                <DatetimeField
                  {...field}
                  noMargin
                  label="Start"
                  onChange={(value) => {
                    field.onChange(value);
                    form.trigger(`items.${index}.endDate`);
                  }}
                />
              )}
            />
          </div>
          <div className={styles.itemDate}>
            <Controller
              name={`items.${index}.endDate`}
              control={form.control}
              render={({ field, fieldState }) => (
                <DatetimeField
                  {...field}
                  noMargin
                  label="Ende"
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
        </>
      )}
      <div className={styles.itemAmount}>
        <Controller
          name={`items.${index}.amount`}
          control={form.control}
          render={({ field }) => (
            <CurrencyInputField {...field} noMargin label="Preis" type="number" />
          )}
        />
      </div>
      <div className={styles.itemRemove}>
        <Button icon={closeIcon} small onClick={onRemove} />
      </div>
    </div>
  );
}

// ─── Existing Customer Editor ────────────────────────────────────────────

function ExistingCustomerEditor({ form }: { form: UseFormReturn<Values> }) {
  return (
    <div className={styles.customerFields}>
      <Controller
        name="updateCustomerNumber"
        control={form.control}
        render={({ field }) => (
          <InputField label="Kundennummer" {...field} />
        )}
      />
      <Controller
        name="updateExistingCustomer.gender"
        control={form.control}
        render={({ field }) => (
          <GenderInputField
            label="Anrede"
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
      <Controller
        name="updateExistingCustomer.firstName"
        control={form.control}
        render={({ field, fieldState }) => (
          <InputField
            label="Vorname"
            error={fieldState.error?.message}
            {...field}
          />
        )}
      />
      <Controller
        name="updateExistingCustomer.lastName"
        control={form.control}
        render={({ field, fieldState }) => (
          <InputField
            label="Nachname"
            error={fieldState.error?.message}
            {...field}
          />
        )}
      />
      <Controller
        name="updateExistingCustomer.email"
        control={form.control}
        render={({ field, fieldState }) => (
          <InputField
            label="E-Mail"
            error={fieldState.error?.message}
            {...field}
          />
        )}
      />
      <Controller
        name="updateExistingCustomer.phone"
        control={form.control}
        render={({ field }) => (
          <InputField label="Telefon" {...field} />
        )}
      />
      <GoogleAddressAutocomplete
        onSelect={(address) => {
          form.setValue('address.streetAndNumber', address.streetAndNumber ?? '');
          form.setValue('address.postalCode', address.postalCode ?? '');
          form.setValue('address.city', address.city ?? '');
        }}
      />
      <Controller
        name="address.streetAndNumber"
        control={form.control}
        render={({ field, fieldState }) => (
          <InputField
            label="Straße"
            error={fieldState.error?.message}
            {...field}
          />
        )}
      />
      <Controller
        name="address.postalCode"
        control={form.control}
        render={({ field, fieldState }) => (
          <InputField
            label="PLZ"
            error={fieldState.error?.message}
            {...field}
          />
        )}
      />
      <Controller
        name="address.city"
        control={form.control}
        render={({ field, fieldState }) => (
          <InputField
            label="Ort"
            error={fieldState.error?.message}
            {...field}
          />
        )}
      />
      <NewCustomerInvoiceAddress form={form} />
    </div>
  );
}

// ─── Invoice Address Sub-Section ────────────────────────────────────────

function NewCustomerInvoiceAddress({
  form,
}: {
  form: UseFormReturn<Values>;
}) {
  const useInvoiceAddress = form.watch('useInvoiceAddress');

  return (
    <div style={{ marginTop: '1rem' }}>
      <Controller
        name="useInvoiceAddress"
        control={form.control}
        render={({ field }) => (
          <Checkbox
            label="Abweichende Rechnungsadresse"
            {...field}
            checked={field.value}
          />
        )}
      />
      {useInvoiceAddress && (
        <div className={styles.customerFields}>
          <GoogleAddressAutocomplete
            onSelect={(address: ParsedAddress) => {
              form.setValue('invoiceAddress.streetAndNumber', address.streetAndNumber ?? '');
              form.setValue('invoiceAddress.postalCode', address.postalCode ?? '');
              form.setValue('invoiceAddress.city', address.city ?? '');
            }}
          />
          <Controller
            name="invoiceAddress.company"
            control={form.control}
            render={({ field, fieldState }) => (
              <InputField
                label="Arbeitgebername"
                required
                error={fieldState.error?.message}
                {...field}
              />
            )}
          />
          <Controller
            name="invoiceAddress.streetAndNumber"
            control={form.control}
            render={({ field, fieldState }) => (
              <InputField
                label="Straße"
                required
                error={fieldState.error?.message}
                {...field}
              />
            )}
          />
          <Controller
            name="invoiceAddress.postalCode"
            control={form.control}
            render={({ field, fieldState }) => (
              <InputField
                label="PLZ"
                required
                error={fieldState.error?.message}
                {...field}
              />
            )}
          />
          <Controller
            name="invoiceAddress.city"
            control={form.control}
            render={({ field, fieldState }) => (
              <InputField
                label="Ort"
                required
                error={fieldState.error?.message}
                {...field}
              />
            )}
          />
          <Controller
            name="invoiceAddress.invoiceEmail"
            control={form.control}
            render={({ field, fieldState }) => (
              <InputField
                label="RG-E-Mail"
                error={fieldState.error?.message}
                {...field}
              />
            )}
          />
        </div>
      )}
    </div>
  );
}
